<template>
  <div
    class="tooltip-container"
    ref="containerRef"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <slot></slot>
    
    <Teleport to="body">
      <Transition name="tooltip-fade">
        <div
          v-if="isVisible && tooltipVisible"
          ref="tooltipRef"
          class="tooltip-popup"
          :style="tooltipPosition"
        >
          <div class="tooltip-content" v-html="tooltipText"></div>
          <div class="tooltip-arrow" :class="arrowClass"></div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, nextTick, computed } from 'vue';

interface Props {
  tooltipText: string;
  tooltipVisible?: boolean;
  disabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  tooltipVisible: true,
  disabled: false,
});

const containerRef = ref<HTMLElement | null>(null);
const tooltipRef = ref<HTMLElement | null>(null);
const isVisible = ref(false);
const placement = ref<'top' | 'bottom'>('top');

const tooltipPosition = ref({
  top: '0px',
  left: '0px',
});

const arrowClass = computed(() => ({
  'arrow-top': placement.value === 'bottom',
  'arrow-bottom': placement.value === 'top',
}));

const isMobile = computed(() => window.innerWidth <= 768);

const handleMouseEnter = () => {
  if (props.disabled || isMobile.value) return;
  
  isVisible.value = true;
  nextTick(() => {
    updatePosition();
  });
};

const handleMouseLeave = () => {
  isVisible.value = false;
};

const updatePosition = () => {
  if (!containerRef.value || !tooltipRef.value) return;

  const container = containerRef.value.getBoundingClientRect();
  const tooltip = tooltipRef.value.getBoundingClientRect();
  
  const SPACING = 8;
  const VIEWPORT_PADDING = 8;
  
  let left = container.left + (container.width / 2) - (tooltip.width / 2);
  
  // 화면 밖으로 나가지 않도록 조정
  if (left < VIEWPORT_PADDING) {
    left = VIEWPORT_PADDING;
  } else if (left + tooltip.width > window.innerWidth - VIEWPORT_PADDING) {
    left = window.innerWidth - tooltip.width - VIEWPORT_PADDING;
  }
  
  const spaceAbove = container.top;
  const spaceBelow = window.innerHeight - container.bottom;
  
  let top = 0;
  
  if (spaceAbove >= tooltip.height + SPACING) {
    // 위쪽에 배치
    top = container.top - tooltip.height - SPACING;
    placement.value = 'top';
  } else if (spaceBelow >= tooltip.height + SPACING) {
    // 아래쪽에 배치
    top = container.bottom + SPACING;
    placement.value = 'bottom';
  } else {
    // 공간이 더 많은 쪽에 배치
    if (spaceAbove > spaceBelow) {
      top = VIEWPORT_PADDING;
      placement.value = 'top';
    } else {
      top = container.bottom + SPACING;
      placement.value = 'bottom';
    }
  }
  
  tooltipPosition.value = {
    top: `${top}px`,
    left: `${left}px`,
  };
};
</script>

<style scoped lang="scss">
.tooltip-container {
  display: inline-block;
  position: relative;
}

.tooltip-popup {
  position: fixed;
  z-index: $z-tooltip;
  pointer-events: none;
}

.tooltip-content {
  background-color: var(--surface-glass-darkest);
  color: var(--color-white);
  font-size: $font-size-psmall;
  line-height: 120%;
  text-align: center;
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
  white-space: pre-wrap;
  max-width: calc(100vw - 16px);
  word-break: keep-all;
}

.tooltip-arrow {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  
  &.arrow-bottom {
    bottom: -5px;
    border-top: 6px solid var(--surface-glass-darkest);
  }
  
  &.arrow-top {
    top: -5px;
    border-bottom: 6px solid var(--surface-glass-darkest);
  }
}

.tooltip-fade-enter-active,
.tooltip-fade-leave-active {
  transition: opacity 0.2s ease;
}

.tooltip-fade-enter-from,
.tooltip-fade-leave-to {
  opacity: 0;
}
</style>